"use client"

import { useQuery } from "@tanstack/react-query"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import moment from "moment"

import { PlanBadge } from "@/metrics/teachers/PlanBadge"

import { Link } from "@/components/ui/Link"
import { LoadingSpinner } from "@/components/LoadingSpinner"
import { Button } from "@/components/ui/Button"

import { TeachersMetricsPageController } from "./TeachersMetricsPageController"
import { TeacherUsageStats } from "../../../app/model/domain/teacher-usage-stats"
import { DataTable } from "../DataTable"

const columnHelper = createColumnHelper<TeacherUsageStats>()

export interface TeachersMetricsPageProps {
  controller: TeachersMetricsPageController
}

export function TeachersMetricsPage({ controller }: TeachersMetricsPageProps) {
  const {
    data: teachersStats,
    isLoading: isteachersStatsLoading,
    isError: isteachersStatsError,
  } = useQuery({
    queryKey: [controller.getTeachersUsageStats.name],
    queryFn: () => controller.getTeachersUsageStats(controller.organizationId),
  })

  const {
    data: organization,
    isLoading: isOrganizationLoading,
    isError: isOrganizationError,
  } = useQuery({
    queryKey: [controller.getOrganization.name],
    queryFn: () => controller.getOrganization(controller.organizationId),
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<TeacherUsageStats, any>[] = [
    columnHelper.accessor("fullName", {
      id: "name",
      header: "Teacher",
    }),
    columnHelper.accessor("isPro", {
      id: "isPro",
      header: "Plan",
      cell: ({ getValue }) => <PlanBadge isPro={getValue()} />,
    }),
    columnHelper.accessor("assignmentsWorkedOnThisYear", {
      id: "assignmentsWorkedOnThisYear",
      header: "Assignments worked",
      sortUndefined: -1,
      cell: ({ getValue }) => getValue()?.toLocaleString() ?? "–",
    }),
    columnHelper.accessor("pagesWorkedOnThisYear", {
      id: "pagesWorkedOnThisYear",
      header: "Student Works Units",
      sortUndefined: -1,
      cell: ({ getValue }) => getValue()?.toLocaleString() ?? "–",
    }),

    columnHelper.accessor("lastLogin", {
      id: "lastLogin",
      header: "Last logged in",
      sortUndefined: -1,
      cell: ({ getValue }) => moment(getValue()).format("MMM D, YYYY") ?? "–",
    }),
  ]

  function handleNudgeClick() {
    controller.nudgeTeachers(organization)
  }

  function handleInviteClick() {
    controller.inviteUsers(organization)
  }

  if (isOrganizationLoading)
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-[calc((100vh-60px)/1.6)]">
        <LoadingSpinner />
      </div>
    )
  if (isOrganizationError)
    return (
      <p className="tw-text-sm tw-text-red-500">{controller.errorMessage}</p>
    )

  const isContractAdmin = controller.isUserContractAdmin(
    organization.contractId,
  )

  return (
    <div className="tw-h-[calc((100vh-60px)/1.6)] tw-flex tw-flex-col tw-p-[1.4em]">
      <div className="tw-mb-4 tw-flex tw-justify-between">
        <div className="tw-my-auto">
          <div className="tw-whitespace-nowrap">
            {organization && !isOrganizationLoading && !isOrganizationError && (
              <h2 className="tw-inline">{organization.name}</h2>
            )}
            <span className="tw-ml-4 tw-text-sm tw-text-subtle">
              {controller.statsTimePeriod} - Present
            </span>
          </div>
          {isContractAdmin && (
            <Link href={`/account/contracts/${organization.contractId}/users`}>
              Manage teachers
            </Link>
          )}
        </div>
        {isContractAdmin ? (
          <Button onClick={handleNudgeClick}>Nudge Teachers</Button>
        ) : (
          <Button onClick={handleInviteClick}>Invite Teachers</Button>
        )}
      </div>
      {isteachersStatsLoading && <p className="tw-text-sm">Loading...</p>}
      {isteachersStatsError && (
        <p className="tw-text-sm tw-text-red-500">{controller.errorMessage}</p>
      )}
      {teachersStats && !isteachersStatsLoading && !isteachersStatsError && (
        <DataTable columns={columns} data={teachersStats} />
      )}
    </div>
  )
}
